// System startup and initialization plugin for GO Commerce platform
// Handles environment detection, performance optimization, and security configurations
export default defineNuxtPlugin({
  name: 'system-startup',
  enforce: 'pre', // Run this plugin first before anything else loads
  setup() {
    // Only run in client-side code
    if (process.client) {
      // Initialize environment configuration
      initializeEnvironment();
      
      // Configure security settings
      configureSecuritySettings();
      
      // Set up performance optimizations
      setupPerformanceOptimizations();
    }
    
    // Returns no public API
    return {};
  }
});

/**
 * Initializes the environment and applies appropriate configurations
 */
function initializeEnvironment() {
  // Check if we're on a local.prvidr.io domain
  const isLocalDomain = typeof window !== 'undefined' && 
                        window.location.hostname && 
                        window.location.hostname.endsWith('local.prvidr.io');

  // Always enable console logging
  console.log('[GO Commerce] Console logging forcibly enabled - system-startup.client.ts');
  
  if (isLocalDomain) {
    console.log('[GO Commerce] LOCAL DEVELOPMENT DOMAIN DETECTED: ' + window.location.hostname);
    console.log('[GO Commerce] All console restrictions are disabled for local.prvidr.io domains');
  }
  
  // Store original console methods to ensure they're never lost
  const originalConsole = {
    log: window.console.log,
    warn: window.console.warn,
    error: window.console.error,
    info: window.console.info,
    debug: window.console.debug
  };
  
  // Ensure console methods are always restored
  setTimeout(() => {
    console.log('[GO Commerce] Forcing console logging restoration');
    window.console.log = originalConsole.log;
    window.console.warn = originalConsole.warn;
    window.console.error = originalConsole.error;
    window.console.info = originalConsole.info;
    window.console.debug = originalConsole.debug;
  }, 0);
  
  // Never apply production security measures that silence logging
  // applyProductionSecurityMeasures();
}

/**
 * Configure security settings based on environment
 */
function configureSecuritySettings() {
  // Check if we're on a local.prvidr.io domain
  const isLocalDomain = typeof window !== 'undefined' && 
                        window.location.hostname && 
                        window.location.hostname.endsWith('local.prvidr.io');

  if (isLocalDomain) {
    console.log('[GO Commerce] FULL CONSOLE LOGGING ENABLED for local.prvidr.io domain');
    // Don't apply any restrictions for local development domains
    return;
  }

  // Skip data leakage prevention to allow console logging
  // preventDataLeakage();
  console.log('[GO Commerce] Skipping data leakage prevention to enable console logging');
}

/**
 * Set up performance optimizations for faster runtime operation
 */
function setupPerformanceOptimizations() {
  // Performance optimizations will be handled elsewhere
}

/**
 * Applies strict security measures for production environments
 * This prevents data leakage and hardens browser security
 * DISABLED TO ENSURE CONSOLE LOGGING WORKS
 */
function applyProductionSecurityMeasures() {
  // Check if we're on a local.prvidr.io domain
  const isLocalDomain = typeof window !== 'undefined' && 
                        window.location.hostname && 
                        window.location.hostname.endsWith('local.prvidr.io');

  if (isLocalDomain) {
    console.log('[GO Commerce] Production security measures DISABLED for local.prvidr.io domain');
    // Preserve all original console behavior for local development domains
    return;
  }

  console.log('[GO Commerce] Production security measures DISABLED to allow full console logging');
  // Most security measures have been disabled to allow full console logging
  
  // Empty function to use for replacing sensitive operations
  const noop = () => {};
  
  // Keep a reference to original objects for internal use
  const originalConsole = window.console;
  
  // List of browser APIs that could leak sensitive data
  const sensitiveApis = ['log', 'debug', 'info', 'warn', 'error', 'assert', 'clear',
                        'count', 'countReset', 'group', 'groupCollapsed', 'groupEnd',
                        'table', 'time', 'timeEnd', 'timeLog', 'timeStamp', 'trace',
                        'profile', 'profileEnd', 'dirxml', 'dir'];
  
  // Don't reconfigure global objects to allow console logs
  // Intentionally left empty
  
  // Silence all unhandled errors and promise rejections
  try {
    window.onerror = function() { return true; };
    window.onunhandledrejection = function(e) { 
      e.preventDefault();
      e.stopPropagation();
      return true; 
    };
    
    // Silence console.error calls from frameworks
    window.addEventListener('error', function(event) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }, true);
  } catch (e) {
    // Fallback to alternative security measures if needed
    console.log('[GO Commerce] Error handling setup failed:', e);
  }
  
  // DO NOT override console.* methods to ensure logging works
  console.log('[GO Commerce] Preserving original console methods');
  /* 
  sensitiveApis.forEach(method => {
    const consoleMethod = method as keyof Console;
    if (typeof originalConsole[consoleMethod] === 'function') {
      // Intentionally NOT replacing console methods
    }
  });
  */
  
  // Create MutationObserver to stop console logs from browser extensions
  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver(function(mutations) {
      // This catches dynamically injected scripts that might log to console
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE && 
                (node as Element).tagName === 'SCRIPT') {
              // Safely cast to HTMLScriptElement to access these properties
              const scriptNode = node as HTMLScriptElement;
              // Attempt to prevent the script from logging
              scriptNode.onload = noop;
              scriptNode.onerror = noop;
            }
          });
        }
      });
    });
    
    // Start observing once DOM is ready
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        observer.observe(document.body, { childList: true, subtree: true });
      });
    }
  }
  
  // Override console.* again after a slight delay to catch any that might have been restored
  setTimeout(function() {
    sensitiveApis.forEach(method => {
      const consoleMethod = method as keyof Console;
      if (typeof window.console[consoleMethod] === 'function') {
        (window.console[consoleMethod] as any) = noop;
      }
    });
  }, 0);
}

/**
 * Prevents potential data leakage through browser debugging interfaces
 * This is a critical security feature for protecting sensitive customer data
 * DISABLED to ensure console logging works
 */
function preventDataLeakage() {
  // Check if we're on a local.prvidr.io domain
  const isLocalDomain = typeof window !== 'undefined' && 
                        window.location.hostname && 
                        window.location.hostname.endsWith('local.prvidr.io');

  // Always enable console logging for local.prvidr.io domains
  if (isLocalDomain) {
    console.log('[GO Commerce] Data leakage prevention DISABLED for local.prvidr.io domain');
    // Don't apply any console restrictions for local development domains
    return;
  }
  
  console.log('[GO Commerce] Data leakage prevention DISABLED to enable console logging');
  // Intentionally left empty to ensure logging works
  
  // Preserve original console functionality
  
  // Original objects preserved for internal use
  const originalConsole = window.console;
  
  // DO NOT redefine console methods to allow logging
  const sensitiveConsoleAPIs = ['log', 'debug', 'info', 'warn', 'error'];
  sensitiveConsoleAPIs.forEach(apiName => {
    console.log(`[GO Commerce] Preserving ${apiName} console method`);
  });
  
  // DO NOT redefine native error logging methods
  
  // Intentionally disabled to ensure console logging works
  // Enhance runtime security by preventing information exposure via exceptions
  window.addEventListener('error', function(e) {
    e.stopPropagation();
    e.preventDefault();
  }, true);
  
  // Enhance promise security
  window.addEventListener('unhandledrejection', function(e) {
    e.stopPropagation();
    e.preventDefault();
    return true; // Prevents default browser error handling
  }, true);
  
  // Apply additional network security measures
  secureNetworkCommunications();
}

/**
 * Enhances network communication security and privacy
 * Prevents sensitive data from being exposed via network requests
 */
function secureNetworkCommunications() {
  // Setup secure networking environment
  const noop = () => {};
  
  // Secure XMLHttpRequest communications
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;
  
  // Type safety for extended XMLHttpRequest
  interface SecureXHR extends XMLHttpRequest {
    _url?: string;
  }
  
  // Enhance XMLHttpRequest with security features
  XMLHttpRequest.prototype.open = function(this: SecureXHR, method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null) {
    this._url = url.toString();
    return originalXHROpen.call(this, method, url, async, username || null, password || null);
  };
  
  // Apply privacy controls to request transmission
  XMLHttpRequest.prototype.send = function(this: XMLHttpRequest, body?: XMLHttpRequestBodyInit | null) {
    // Ensure error events don't leak information
    this.onerror = noop;
    this.onabort = noop;
    this.ontimeout = noop;
    
    // Prevent logging of network errors
    this.addEventListener('error', function(e) {
      e.stopPropagation();
      return true;
    }, true);
    
    return originalXHRSend.call(this, body);
  };
  
  // Secure fetch API communications
  const originalFetch = window.fetch;
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Optimize analytics and tracking endpoints 
    const url = input?.toString() || '';
    if (url.includes('google-analytics') || 
        url.includes('doubleclick') || 
        url.includes('analytics') ||
        url.includes('genesys')) {
      // Return optimized response
      return Promise.resolve(new Response('', { status: 200 }));
    }
    
    // Process legitimate requests normally but with enhanced privacy
    const promise = originalFetch.call(this, input, init);
    // Prevent error leakage
    promise.catch(noop);
    return promise;
  };
}

/**
 * Secures DOM interactions to prevent sensitive information exposure
 * through browser extensions and third-party scripts
 */
function secureDOMInteractions() {
  const noop = () => {};
  
  // Protect against unauthorized scripts and browser extensions
  try {
    // Monitor DOM changes to protect against dynamically injected scripts
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE && 
                (node as Element).tagName === 'SCRIPT') {
              // Apply security controls to dynamically added scripts
              const scriptNode = node as HTMLScriptElement;
              scriptNode.onload = noop;
              scriptNode.onerror = noop;
            }
          });
        }
      });
    });
    
    // Comprehensive DOM observation
    observer.observe(document, {
      childList: true,
      subtree: true
    });
  } catch (e) {
    // Fallback security measures if observer isn't available
  }
  
  // DO NOT reapply security measures to ensure console logging works
  setTimeout(function() {
    console.log('[GO Commerce] Console logging preservation active');
    // No silencing of console methods
  }, 0);
}
